import { useSelector } from 'react-redux';

const TIME_ZONE = 'America/Sao_Paulo';
const DEFAULT_LOCALE = 'pt-br';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const MONTH = DAY * 30;
const YEAR = DAY * 365;

const long_date_format: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  timeZone: TIME_ZONE,
};

const long_date_time_format: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: TIME_ZONE,
};

const short_date_format: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: TIME_ZONE,
};

const short_time_format: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: TIME_ZONE,
};

interface IntlState {
  locale?: string;
}

interface RootState {
  intl?: IntlState;
  [key: string]: unknown;
}

const toDate = (value: string | Date): Date =>
  typeof value === 'string' ? new Date(value) : value;

const capitalize = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Formatação de datas do site (pt-BR, timezone America/Sao_Paulo),
 * centralizando os padrões hoje duplicados em NewsCard, NewsListView,
 * NewsView, Attachments e RelatedItems. Locale vem do Redux (state.intl.locale)
 * para acompanhar o idioma ativo, igual ao FormattedDate do Volto core.
 */
export const useFormattedDate = () => {
  const locale =
    useSelector((state: RootState) => state.intl?.locale) || DEFAULT_LOCALE;

  const formatLong = (value: string | Date): string =>
    new Intl.DateTimeFormat(locale, long_date_format).format(toDate(value));

  const formatLongWithTime = (value: string | Date): string =>
    new Intl.DateTimeFormat(locale, long_date_time_format).format(
      toDate(value),
    );

  // "20/05/2026"
  const formatShortDate = (value: string | Date): string =>
    new Intl.DateTimeFormat(locale, short_date_format).format(toDate(value));

  // "08:19"
  const formatShortTime = (value: string | Date): string =>
    new Intl.DateTimeFormat(locale, short_time_format).format(toDate(value));

  // "20/05/2026 às 08:19"
  const formatShortWithTime = (value: string | Date): string =>
    `${formatShortDate(value)} às ${formatShortTime(value)}`;

  // "há 42 minutos" / "há 2 horas" (ou "Agora" perto do momento atual).
  // Usa floor por unidade (não round) para não pular, por exemplo, de
  // "42 minutos" direto para "1 hora".
  const formatRelative = (
    value: string | Date,
    { capitalize: shouldCapitalize = false }: { capitalize?: boolean } = {},
  ): string => {
    const deltaMs = toDate(value).getTime() - Date.now();
    const absMs = Math.abs(deltaMs);
    const sign = deltaMs < 0 ? -1 : 1;
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });

    let text: string;
    if (absMs < MINUTE) {
      text = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
        0,
        'second',
      );
    } else if (absMs < HOUR) {
      text = rtf.format(sign * Math.floor(absMs / MINUTE), 'minute');
    } else if (absMs < DAY) {
      text = rtf.format(sign * Math.floor(absMs / HOUR), 'hour');
    } else if (absMs < MONTH) {
      text = rtf.format(sign * Math.floor(absMs / DAY), 'day');
    } else if (absMs < YEAR) {
      text = rtf.format(sign * Math.floor(absMs / MONTH), 'month');
    } else {
      text = rtf.format(sign * Math.floor(absMs / YEAR), 'year');
    }

    return shouldCapitalize ? capitalize(text) : text;
  };

  return {
    formatLong,
    formatLongWithTime,
    formatShortDate,
    formatShortTime,
    formatShortWithTime,
    formatRelative,
  };
};

export default useFormattedDate;
